import { Logger } from '@notificare/logger';

const serviceWorkerLogger = new Logger('notificare/push/sw');

function sleep(milliseconds) {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
}

let hasReadyClients = false;
function onMessage(event) {
    if (!event.data)
        return;
    try {
        switch (event.data.action) {
            case 're.notifica.ready':
                hasReadyClients = true;
                break;
            default:
                serviceWorkerLogger.warning('Unknown service worker message event: ', event);
        }
    }
    catch (e) {
        serviceWorkerLogger.error('Failed to process a service worker message event: ', e);
    }
}
async function onPush(event) {
    var _a, _b, _c, _d, _e;
    if (!event.data) {
        serviceWorkerLogger.warning('The push event contained no data. Skipping...');
        return;
    }
    let workerNotification;
    try {
        workerNotification = event.data.json();
    }
    catch (e) {
        serviceWorkerLogger.error('Unable to parse the push event data.');
        // TODO: Consider preserving the v2 'workerpush' post message.
        return;
    }
    serviceWorkerLogger.info(workerNotification);
    if (workerNotification['x-sender'] !== 'notificare') {
        serviceWorkerLogger.debug('Processing an unknown notification.');
        // TODO: consider sending the event to the worker instead.
        const clients = await self.clients.matchAll();
        clients.forEach((client) => {
            client.postMessage({
                cmd: 'unknownpush',
                message: workerNotification,
            });
        });
        return;
    }
    const notification = workerNotification;
    if (notification.system) {
        serviceWorkerLogger.debug('Processing a system notification.', notification);
        // TODO: consider sending the event to the worker instead.
        const clients = await self.clients.matchAll();
        clients.forEach((client) => {
            client.postMessage({
                cmd: 'system',
                message: workerNotification,
            });
        });
        return;
    }
    const clients = await self.clients.matchAll();
    clients.forEach((client) => {
        client.postMessage({
            cmd: 'notificationreceive',
            message: workerNotification,
        });
    });
    if (!notification.push || !notification.alert) {
        // Silent notification.
        return;
    }
    const { badge } = notification;
    if (badge) {
        if (navigator.setAppBadge)
            navigator.setAppBadge(badge);
        if (navigator.setClientBadge)
            navigator.setClientBadge(badge);
    }
    const title = (_b = (_a = notification.alertTitle) !== null && _a !== void 0 ? _a : notification.application) !== null && _b !== void 0 ? _b : '';
    let { icon } = notification;
    // When the browser doesn't support the image attribute, use the attachment as the icon when available.
    if (!('image' in Notification.prototype) && ((_c = notification.attachment) === null || _c === void 0 ? void 0 : _c.uri)) {
        icon = notification.attachment.uri;
    }
    const options = {
        tag: notification.id,
        body: notification.alert,
        icon,
        image: (_d = notification.attachment) === null || _d === void 0 ? void 0 : _d.uri,
        requireInteraction: notification.requireInteraction,
        renotify: notification.renotify,
        actions: (_e = notification.actions) === null || _e === void 0 ? void 0 : _e.map((action) => ({
            // eslint-disable-next-line no-underscore-dangle
            action: action._id,
            title: action.label,
            icon: action.icon,
        })),
        data: notification,
    };
    await self.registration.showNotification(title, options);
}
async function onNotificationClick(event) {
    event.notification.close();
    await ensureOpenWindowClient();
    const clients = await self.clients.matchAll({ type: 'window' });
    if (!clients.length) {
        serviceWorkerLogger.error('Unable to process the notification click without an active client.');
        throw new Error('Unable to process the notification click without an active client.');
    }
    const client = clients[0];
    if (event.action) {
        client.postMessage({
            cmd: 'notificationreply',
            notification: event.notification.data,
            action: event.action,
        });
    }
    else {
        client.postMessage({
            cmd: 'notificationclick',
            notification: event.notification.data,
        });
    }
    try {
        await client.focus();
    }
    catch (e) {
        serviceWorkerLogger.error('Failed to focus client: ', client, e);
    }
}
async function ensureOpenWindowClient() {
    const clients = await self.clients.matchAll({ type: 'window' });
    if (clients.length) {
        if (hasReadyClients)
            return;
        await waitForOpenWindowClient();
        return;
    }
    // Reset the readiness state in case the service worker instance is reused across
    // application restarts, when it used to be ready.
    hasReadyClients = false;
    // const url = event.notification.data.urlFormatString.replace("%@", event.notification.tag);
    await self.clients.openWindow('/');
    await waitForOpenWindowClient();
}
async function waitForOpenWindowClient() {
    const clients = await self.clients.matchAll({ type: 'window' });
    if (clients.length && hasReadyClients)
        return;
    await sleep(1000);
    await waitForOpenWindowClient();
}

self.addEventListener('install', (event) => {
    serviceWorkerLogger.debug('Service worker installed.');
    event.waitUntil(self.skipWaiting()); // Activate worker immediately.
    // NOTE: without this install + activate setup, the service worker controller
    // will not be available on the website to send post messages to the worker.
});
self.addEventListener('activate', (event) => {
    serviceWorkerLogger.debug('Service worker activated.');
    event.waitUntil(self.clients.claim()); // Become available to all pages.
    // NOTE: without this install + activate setup, the service worker controller
    // will not be available on the website to send post messages to the worker.
});
self.addEventListener('message', (event) => {
    serviceWorkerLogger.debug('Handling a service worker message event.');
    onMessage(event);
});
self.addEventListener('push', (event) => {
    serviceWorkerLogger.debug('Handling a push notification event.');
    event.waitUntil(onPush(event));
});
self.addEventListener('pushsubscriptionchange', () => {
    serviceWorkerLogger.debug('Handling a push subscription change event.');
});
self.addEventListener('notificationclick', (event) => {
    serviceWorkerLogger.debug('Handling a notification click event.');
    event.waitUntil(onNotificationClick(event));
});
// We need an export to force this file to act like a module, so TS will let us re-type `self`.
var index = null;

export { index as default };
//# sourceMappingURL=index.js.map
